import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Award, Heart, Gem } from 'lucide-react';
import { SEO } from '../components/SEO';
import { ContactDialog } from '../components/ContactDialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const About = () => {
  const { t, i18n } = useTranslation();
  const [contactOpen, setContactOpen] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages-content`);
      if (response.data && response.data.pages && response.data.pages.about) {
        setPageContent(response.data.pages.about);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  // Хелпер для получения текста с фоллбэком
  const getText = (field, fallbackRu, fallbackLv) => {
    if (pageContent && pageContent[field]) {
      return pageContent[field][lang] || pageContent[field].ru || (lang === 'lv' ? fallbackLv : fallbackRu);
    }
    return lang === 'lv' ? fallbackLv : fallbackRu;
  };

  const features = [
    {
      icon: Gem,
      title: getText('feature1_title', 'Кастомные дизайны', 'Pielāgoti dizaini'),
      description: getText('feature1_desc', 'Создаём уникальные украшения по вашим пожеланиям', 'Izveidojam unikālus juvelierizstrādājumus pēc jūsu vēlmēm')
    },
    {
      icon: Heart,
      title: getText('feature2_title', 'Помолвочные кольца', 'Saderināšanās gredzeni'),
      description: getText('feature2_desc', 'Наша специальность - идеальные кольца для вашего важного момента', 'Mūsu specialitāte - ideālie gredzeni jūsu svarīgākajam brīdim')
    },
    {
      icon: Award,
      title: getText('feature3_title', 'Лучшие цены в Риге', 'Labākās cenas Rīgā'),
      description: getText('feature3_desc', 'Предлагаем премиум качество по доступным ценам', 'Piedāvājam premium kvalitāti par pieejamām cenām')
    },
    {
      icon: Sparkles,
      title: getText('feature4_title', 'В наличии и под заказ', 'Pieejami un pēc pasūtījuma'),
      description: getText('feature4_desc', 'Выбирайте из каталога или закажите уникальное изделие', 'Izvēlieties no kataloga vai pasūtiet unikālu izstrādājumu')
    }
  ];

  const steps = [
    {
      num: 1,
      title: getText('step1_title', 'Посмотрите каталог', 'Apskati katalogu'),
      desc: getText('step1_desc', 'Ознакомьтесь с нашими изделиями в наличии на сайте', 'Iepazīsties ar mūsu pieejamajiem izstrādājumiem vietnē')
    },
    {
      num: 2,
      title: getText('step2_title', 'Свяжитесь с нами', 'Sazinies ar mums'),
      desc: getText('step2_desc', 'Расскажите о ваших пожеланиях или закажите кастомный дизайн', 'Pastāsti par savām vēlmēm vai pasūti pielāgotu dizainu')
    },
    {
      num: 3,
      title: getText('step3_title', 'Получите ваше кольцо', 'Saņem savu gredzenu'),
      desc: getText('step3_desc', 'Мы создадим идеальное украшение именно для вас', 'Mēs radīsim ideālo juvelierizstrādājumu tieši jums')
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="about-page">
      <SEO 
        title={getText('page_title', 'О нас', 'Par mums')}
        description={getText('page_description', 
          'Pure Atelier Riga - молодая компания, специализирующаяся на кастомных ювелирных изделиях и помолвочных кольцах',
          'Pure Atelier Riga - jauns uzņēmums, kas specializējas pielāgotos juvelierizstrādājumos un saderināšanās gredzenos'
        )}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl mb-6 tracking-tight" data-testid="about-title">
            {getText('page_title', 'О Pure Atelier', 'Par Pure Atelier')}
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {getText('page_description',
              'Молодая компания в Риге, специализирующаяся на кастомных ювелирных изделиях и помолвочных кольцах',
              'Jauns uzņēmums Rīgā, kas specializējas pielāgotos juvelierizstrādājumos un saderināšanās gredzenos'
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-gradient-to-br from-accent/5 to-transparent p-12 rounded-sm border border-accent/20 mb-16"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl mb-6 tracking-tight text-center">
              {getText('story_title', 'Наша история', 'Mūsu stāsts')}
            </h2>
            <div className="space-y-4 font-body text-lg leading-relaxed text-muted-foreground">
              <p>
                {getText('story_text1',
                  'Pure Atelier Riga — молодая и динамичная компания в мире ювелирных изделий Риги. Мы специализируемся на создании кастомных украшений, уделяя особое внимание помолвочным кольцам.',
                  'Pure Atelier Riga ir jauns un dinamisks uzņēmums Rīgas juvelierizstrādājumu pasaulē. Mēs specializējamies pielāgotu juvelierizstrādājumu izgatavošanā, īpaši pievēršot uzmanību saderināšanās gredzeniem.'
                )}
              </p>
              <p>
                {getText('story_text2',
                  'Мы работаем под заказ - вы можете выбрать любую оправу, любой камень и любую чистоту. Каждое кольцо создаётся специально для вас, отражая вашу уникальную историю и стиль.',
                  'Mēs strādājam pēc pasūtījuma - jūs varat izvēlēties jebkuru ietvaru, jebkuru akmeni un jebkuru tīrību. Katrs gredzens tiek radīts īpaši jums, atspoguļojot jūsu unikālo stāstu un stilu.'
                )}
              </p>
              <p>
                {getText('story_text3',
                  'Наша цель — предлагать украшения высочайшего качества по лучшим ценам в Риге. Мы верим, что роскошь и совершенство должны быть доступны каждому.',
                  'Mūsu mērķis ir piedāvāt augstākās kvalitātes juvelierizstrādājumus par labākajām cenām Rīgā. Mēs ticam, ka luksuss un izcilība ir pieejama ikvienam.'
                )}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                className="bg-background p-8 rounded-sm border border-border hover:border-accent/50 transition-colors duration-300"
              >
                <Icon className="w-12 h-12 text-accent mb-4" />
                <h3 className="font-heading text-2xl mb-3 tracking-tight">{feature.title}</h3>
                <p className="font-body text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="bg-background p-12 rounded-sm border border-border text-center"
        >
          <h2 className="font-heading text-3xl md:text-4xl mb-6 tracking-tight">
            {getText('how_it_works_title', 'Как это работает?', 'Kā tas darbojas?')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {steps.map((step) => (
              <div key={step.num}>
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-heading text-2xl text-accent">{step.num}</span>
                </div>
                <h3 className="font-body font-medium text-lg mb-2 uppercase tracking-wide">
                  {step.title}
                </h3>
                <p className="font-body text-sm text-muted-foreground">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
          <Button 
            size="lg"
            onClick={() => setContactOpen(true)}
            className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
          >
            {lang === 'lv' ? 'Sazināties ar mums' : 'Связаться с нами'}
          </Button>
        </motion.div>
      </div>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
};
